import type { VoucherApproval } from './fraud.js';

/**
 * 전자바우처 포털 승인내역 표(엑셀·CSV 를 문자열 2차원 배열로 읽은 것)를 대조용 승인내역으로 바꾼다.
 * 포털·지자체마다 열 이름이 조금씩 달라 머리행을 이름 후보로 찾는다. 파일은 브라우저에서만 읽고,
 * 서버에는 이용자 id 로 바뀐 승인내역만 보낸다(이름·생년월일은 보내지 않는다).
 */

const HEADERS = {
  approvalNo: ['승인번호', '결제번호', '거래번호'],
  dateTime: ['승인일시', '결제일시', '거래일시', '승인일자시간'],
  date: ['승인일자', '결제일자', '거래일자', '승인일', '결제일', '서비스일자', '제공일자'],
  time: ['승인시간', '결제시간', '거래시간', '승인시각', '결제시각'],
  name: ['대상자명', '이용자명', '수급자명', '대상자', '이용자', '성명', '아동명'],
  birth: ['생년월일', '대상자생년월일', '이용자생년월일'],
  staff: ['제공인력명', '제공인력', '제공자명', '제공자', '치료사', '치료사명'],
  amount: ['결제금액', '승인금액', '총결제금액', '바우처금액', '금액'],
  program: ['사업명', '서비스명', '바우처사업명', '사업', '서비스'],
} as const;
type Col = keyof typeof HEADERS;

const squash = (s: string) => s.replace(/[\s()·_\-/]/g, '');

export interface SheetRef {
  clients: ReadonlyArray<{ id: string; displayName: string; birthYear: number }>;
  staff: ReadonlyArray<{ userId: string; name: string }>;
}

export interface SheetRow {
  line: number;             // 파일의 행 번호(1부터, 머리행 포함)
  approvalNo: string;
  name: string;
  approvedAt?: string;      // ISO(+09:00)
  amount?: number;
  program: string;
  clientId?: string;        // 이름(+출생연도)으로 하나로 정해졌을 때
  candidates: string[];     // 이름이 같은 이용자(여럿이면 사람이 고른다)
  therapistId?: string;
  problem?: string;         // 대조에서 빼는 이유(날짜·금액을 못 읽음 등)
}

export interface SheetResult { rows: SheetRow[]; months: string[]; error?: string }

/** 머리행: 처음 15행 안에서 승인번호와 날짜 열이 모두 있는 행. */
function findHeader(table: string[][]): { at: number; cols: Partial<Record<Col, number>> } | null {
  for (let at = 0; at < Math.min(15, table.length); at++) {
    const cells = (table[at] ?? []).map((c) => squash(String(c ?? '')));
    const cols: Partial<Record<Col, number>> = {};
    for (const key of Object.keys(HEADERS) as Col[]) {
      // 정확히 같은 이름을 먼저, 없으면 앞부분이 같은 이름('결제금액(원)')
      const names = HEADERS[key].map(squash);
      let i = cells.findIndex((c) => names.includes(c));
      if (i < 0) i = cells.findIndex((c) => names.some((n) => c.startsWith(n)));
      if (i >= 0 && !Object.values(cols).includes(i)) cols[key] = i;
    }
    if (cols.approvalNo !== undefined && (cols.dateTime !== undefined || cols.date !== undefined)) return { at, cols };
  }
  return null;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** 엑셀 날짜 일련번호(1900 체계)·여러 날짜 표기를 KST ISO 로. 시간이 따로 오면 합친다. */
export function parseKstDateTime(dateRaw: string, timeRaw = ''): string | undefined {
  const d = dateRaw.trim(), t = timeRaw.trim();
  let y: number, mo: number, da: number, h = 0, mi = 0, s = 0;
  const serial = /^\d{5}(\.\d+)?$/.test(d) ? Number(d) : NaN;
  if (!Number.isNaN(serial)) {
    const ms = Math.round((serial - 25569) * 86400_000); // 1970-01-01 = 25569
    const x = new Date(ms);
    [y, mo, da, h, mi, s] = [x.getUTCFullYear(), x.getUTCMonth() + 1, x.getUTCDate(), x.getUTCHours(), x.getUTCMinutes(), x.getUTCSeconds()];
  } else {
    const m = d.match(/^(\d{4})[-./년\s]*(\d{1,2})[-./월\s]*(\d{1,2})일?(?:[T\s]+(\d{1,2}):?(\d{2}):?(\d{2})?)?/)
      ?? d.match(/^(\d{4})(\d{2})(\d{2})(?:(\d{2})(\d{2})(\d{2})?)?$/);
    if (!m) return undefined;
    [y, mo, da] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (m[4] !== undefined) [h, mi, s] = [Number(m[4]), Number(m[5]), Number(m[6] ?? 0)];
  }
  if (t) {
    const tm = t.match(/^(\d{1,2}):?(\d{2}):?(\d{2})?$/);
    const ts = /^0?\.\d+$/.test(t) ? Math.round(Number(t) * 86400) : NaN; // 엑셀 시간 일련번호
    if (tm) [h, mi, s] = [Number(tm[1]), Number(tm[2]), Number(tm[3] ?? 0)];
    else if (!Number.isNaN(ts)) [h, mi, s] = [Math.floor(ts / 3600), Math.floor(ts / 60) % 60, ts % 60];
    else return undefined;
  }
  const iso = `${y}-${pad(mo)}-${pad(da)}T${pad(h)}:${pad(mi)}:${pad(s)}+09:00`;
  const check = new Date(iso);
  if (Number.isNaN(check.getTime()) || mo > 12 || da > 31 || h > 23 || mi > 59 || s > 59) return undefined;
  // 2월 30일 같은 날짜는 Date 가 다음 달로 넘기므로 되돌려 확인
  const back = new Date(check.getTime() + 9 * 3600_000);
  if (back.getUTCDate() !== da) return undefined;
  return iso;
}

const parseAmount = (raw: string): number | undefined => {
  const v = raw.replace(/[,\s원₩]/g, '');
  return /^-?\d+(\.0+)?$/.test(v) ? Math.round(Number(v)) : undefined;
};

/** 생년월일 칸에서 출생연도만(주민번호 앞자리 'YYMMDD' 도 받는다). 나머지는 쓰지 않는다. */
function birthYearOf(raw: string, approvedYear: number): number | undefined {
  const v = raw.replace(/\D/g, '');
  if (v.length >= 8 && /^(19|20)\d{2}/.test(v)) return Number(v.slice(0, 4));
  if (v.length === 6 || v.length === 7) {
    const yy = Number(v.slice(0, 2));
    return 2000 + yy <= approvedYear ? 2000 + yy : 1900 + yy;
  }
  return undefined;
}

const sameName = (a: string, b: string) => a.replace(/\s/g, '') === b.replace(/\s/g, '');

export function readVoucherSheet(table: string[][], ref: SheetRef): SheetResult {
  const head = findHeader(table);
  if (!head) return { rows: [], months: [], error: '승인번호와 승인일자(또는 승인일시) 열을 찾지 못했습니다. 포털에서 내려받은 승인내역 파일인지 확인하세요.' };
  const { at, cols } = head;
  if (cols.name === undefined) return { rows: [], months: [], error: '대상자명(이용자명) 열을 찾지 못했습니다.' };
  const cell = (r: string[], c?: number) => (c === undefined ? '' : String(r[c] ?? '').trim());

  const rows: SheetRow[] = [];
  const seen = new Set<string>();
  for (let i = at + 1; i < table.length; i++) {
    const r = table[i] ?? [];
    const approvalNo = cell(r, cols.approvalNo);
    const name = cell(r, cols.name);
    if (!approvalNo && !name) continue;          // 빈 줄
    if (/^(합계|소계|총계)/.test(approvalNo) || /^(합계|소계|총계)/.test(name)) continue;
    const approvedAt = cols.dateTime !== undefined ? parseKstDateTime(cell(r, cols.dateTime)) : parseKstDateTime(cell(r, cols.date), cell(r, cols.time));
    const amount = cols.amount === undefined ? undefined : parseAmount(cell(r, cols.amount));
    const program = cell(r, cols.program);
    const row: SheetRow = { line: i + 1, approvalNo, name, approvedAt, amount, program, candidates: [] };

    const year = approvedAt ? Number(approvedAt.slice(0, 4)) : new Date().getUTCFullYear();
    const by = cols.birth === undefined ? undefined : birthYearOf(cell(r, cols.birth), year);
    const named = ref.clients.filter((c) => sameName(c.displayName, name));
    const exact = by === undefined ? named : named.filter((c) => c.birthYear === by);
    row.candidates = (exact.length ? exact : named).map((c) => c.id);
    if (row.candidates.length === 1) row.clientId = row.candidates[0];

    const staffName = cell(r, cols.staff);
    if (staffName) {
      const st = ref.staff.filter((s) => sameName(s.name, staffName));
      if (st.length === 1) row.therapistId = st[0]!.userId;
    }

    if (!approvalNo) row.problem = '승인번호가 비어 있습니다.';
    else if (seen.has(approvalNo)) row.problem = '같은 승인번호가 이미 있습니다.';
    else if (!approvedAt) row.problem = '승인 일시를 읽지 못했습니다.';
    else if (amount === undefined) row.problem = '결제금액을 읽지 못했습니다.';
    else if (amount <= 0) row.problem = '취소·환불 건은 대조에서 뺍니다.';
    seen.add(approvalNo);
    rows.push(row);
  }
  const count = new Map<string, number>();
  for (const r of rows) if (r.approvedAt && !r.problem) count.set(r.approvedAt.slice(0, 7), (count.get(r.approvedAt.slice(0, 7)) ?? 0) + 1);
  const months = [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([m]) => m);
  return { rows, months };
}

/** 대조에 보낼 수 있는 행(이용자가 정해지고, 문제가 없고, 고른 달). */
export function sheetApprovals(rows: readonly SheetRow[], month: string, chosen: ReadonlyMap<number, string> = new Map()): VoucherApproval[] {
  return rows.flatMap((r) => {
    const clientId = r.clientId ?? chosen.get(r.line);
    if (r.problem || !clientId || !r.approvedAt || r.amount === undefined || !r.approvedAt.startsWith(month)) return [];
    return [{ approvalNo: r.approvalNo, clientId, approvedAt: r.approvedAt, amount: r.amount, program: r.program || '바우처', ...(r.therapistId ? { therapistId: r.therapistId } : {}) }];
  });
}
